// Tile size in pixels (each tile is 16x16 in the spritesheet)
export const SPRITE_TILE_SIZE = 16

// Tileset sheet paths (legacy system - used by level1-tilemap.ts)
// New TMX-based system uses paths defined in TMX files
export const TILESET_SHEETS = {
  ground: "/images/biomes/grassland/ground_grasss.png",
  treesRocks: "/images/biomes/grassland/Trees_rocks.png",
  waterCoasts: "/images/biomes/grassland/Water_coasts.png",
  details: "/images/biomes/grassland/Details.png",
  waterShimmer1: "/images/biomes/grassland/water_detilazation.png",
  waterShimmer2: "/images/biomes/grassland/water_detilazation_v2.png",
}

// Ground tile types
export type GroundTileType = "grass" | "path"

// Sprite coordinates in tileset (x, y in tile units)
export interface SpriteCoords {
  x: number
  y: number
  width?: number
  height?: number
}

// Each logical 32px tile is a 2x2 grid of 16px subtiles
// [row][col] - [0][0]=NW, [0][1]=NE, [1][0]=SW, [1][1]=SE
export type SubtileGrid = [[SpriteCoords, SpriteCoords], [SpriteCoords, SpriteCoords]]

/**
 * Grassland spritesheet analysis (ground_grasss.png):
 * Image is 848x224 pixels = 53 columns x 14 rows of 16x16 tiles
 *
 * TMX Tile ID to sprite coordinate conversion:
 *   Column = (tileId - 1) % 53
 *   Row = Math.floor((tileId - 1) / 53)
 *
 * Key tiles from TMX "main space" layer:
 * - Tile 56 (col 2, row 1): Solid grass
 * - Tile 55 (col 1, row 1): Path with grass edge on WEST (left)
 * - Tile 57 (col 3, row 1): Path with grass edge on EAST (right)
 * - Tile 109 (col 2, row 2): Path with grass edge on NORTH (top)
 * - Tile 162 (col 2, row 3): Path with grass edge on SOUTH (bottom)
 * - Tile 434 (col 9, row 8): Center dirt (no grass edges)
 *
 * For 2-tile wide paths:
 * - Vertical path: left tile uses 55 (grass on W), right tile uses 57 (grass on E)
 * - Horizontal path: top tile uses 109 (grass on N), bottom tile uses 162 (grass on S)
 */

// Grass tiles - tile 56 and variants (col 2, row 1 area)
// TMX uses tile 56 extensively for grass, which is at (2, 1)
export const GRASS_TILES: SpriteCoords[] = [
  { x: 2, y: 1 }, // Tile 56 - main grass
]

/**
 * Get a grass tile with seeded random variation
 */
export function getGrassTileCoords(x: number, y: number): SpriteCoords {
  const index = Math.abs((x * 31 + y * 17) % GRASS_TILES.length)
  return GRASS_TILES[index]
}

/**
 * Get the sprite coordinates for a path tile based on neighboring tiles.
 *
 * TMX-derived tile mappings for 2-tile wide paths:
 * - Tile 55 (1, 1): Path with grass edge on WEST (for right side of vertical path)
 * - Tile 57 (3, 1): Path with grass edge on EAST (for left side of vertical path)
 * - Tile 60 (6, 1): Path with grass on EAST edge (alternate)
 * - Tile 5 (4, 0): Corner/transition tile
 * - Tile 109 (2, 2): Transition with grass on NORTH
 * - Tile 162 (2, 3): Transition with grass on SOUTH
 *
 * For a 2-tile wide path:
 * - Vertical path: left tile has grass on W (use tile at (1,1)), right tile has grass on E (use tile at (3,1))
 * - Horizontal path: top tile has grass on N, bottom tile has grass on S
 *
 * @param hasN - Is North neighbor a PATH tile?
 * @param hasE - Is East neighbor a PATH tile?
 * @param hasS - Is South neighbor a PATH tile?
 * @param hasW - Is West neighbor a PATH tile?
 */
export function getPathTileCoords(
  hasN: boolean,
  hasE: boolean,
  hasS: boolean,
  hasW: boolean,
  hasNE: boolean = true,
  hasSE: boolean = true,
  hasSW: boolean = true,
  hasNW: boolean = true,
): SpriteCoords {
  // TMX-derived coordinates for path tiles
  // Tile semantics (CORRECTED):
  // - Tile 55 (1,1): Grass on EAST edge
  // - Tile 57 (3,1): Grass on WEST edge
  // - Tile 109 (2,2): Grass on NORTH edge
  // - Tile 162 (2,3): Grass on SOUTH edge

  // === INNER CORNERS (path on all 4 cardinal sides + 1 diagonal missing) ===
  // These create the "notched" appearance at path junctions
  // The sprite should have grass nibble WHERE the diagonal is missing

  // Inner corner NW: grass diagonal to NW (path N,E,S,W but no NW)
  if (hasN && hasE && hasS && hasW && !hasNW && hasNE && hasSE && hasSW) {
    return { x: 1, y: 2 } // grass nibble in NW
  }
  // Inner corner NE: grass diagonal to NE
  if (hasN && hasE && hasS && hasW && hasNW && !hasNE && hasSE && hasSW) {
    return { x: 3, y: 2 } // grass nibble in NE
  }
  // Inner corner SW: grass diagonal to SW
  if (hasN && hasE && hasS && hasW && hasNW && hasNE && hasSE && !hasSW) {
    return { x: 4, y: 4 } // grass nibble in SW
  }
  // Inner corner SE: grass diagonal to SE
  if (hasN && hasE && hasS && hasW && hasNW && hasNE && !hasSE && hasSW) {
    return { x: 5, y: 4 } // grass nibble in SE
  }

  // === OUTER CORNERS (path on 2 adjacent cardinal sides only) ===

  // NW outer corner: grass to N and W, path goes E and S
  if (!hasN && hasE && hasS && !hasW) {
    return { x: 4, y: 0 } // dirt wedge SE
  }
  // NE outer corner: grass to N and E, path goes W and S
  if (!hasN && !hasE && hasS && hasW) {
    return { x: 5, y: 0 } // dirt wedge SW
  }
  // SW outer corner: grass to S and W, path goes N and E
  if (hasN && hasE && !hasS && !hasW) {
    return { x: 4, y: 1 } // dirt wedge NE
  }
  // SE outer corner: grass to S and E, path goes N and W
  if (hasN && !hasE && !hasS && hasW) {
    return { x: 5, y: 1 } // dirt wedge NW
  }

  // === EDGE TILES (path on 3 cardinal sides, grass on 1) ===

  // West edge: grass to WEST (tile 57 shows grass on left side)
  if (hasN && hasE && hasS && !hasW) {
    return { x: 3, y: 1 } // Tile 57 - grass on west
  }
  // East edge: grass to EAST (tile 55 shows grass on right side)
  if (hasN && !hasE && hasS && hasW) {
    return { x: 1, y: 1 } // Tile 55 - grass on east
  }
  // North edge: grass to NORTH (tile 109 shows grass on top)
  if (!hasN && hasE && hasS && hasW) {
    return { x: 2, y: 2 } // Tile 109 - grass on north
  }
  // South edge: grass to SOUTH (tile 162 shows grass on bottom)
  if (hasN && hasE && !hasS && hasW) {
    return { x: 2, y: 3 } // Tile 162 - grass on south
  }

  // === CENTER (surrounded by path on all sides) ===
  if (hasN && hasE && hasS && hasW) {
    return { x: 9, y: 8 } // Tile 434 - solid dirt
  }

  // Default fallback
  return { x: 9, y: 8 }
}

// ============================================
// SUBTILE-BASED AUTOTILING (16px native rendering)
// ============================================

// Standard sprite coords for common tiles
const GRASS_SPRITE: SpriteCoords = { x: 2, y: 1 } // Tile 56 - solid grass
const DIRT_SPRITE: SpriteCoords = { x: 9, y: 8 } // Tile 434 - solid dirt

// Edge tiles (grass on one side)
const EDGE_W: SpriteCoords = { x: 3, y: 1 } // Tile 57 - grass on west
const EDGE_E: SpriteCoords = { x: 1, y: 1 } // Tile 55 - grass on east
const EDGE_N: SpriteCoords = { x: 2, y: 2 } // Tile 109 - grass on north
const EDGE_S: SpriteCoords = { x: 2, y: 3 } // Tile 162 - grass on south

// Outer corner tiles, named for the two grass-facing sides.
// VERIFIED against the sheet: the art's convex corners are mostly-grass tiles
// with a dirt wedge entering from the path side. The previous SW/SE coords
// (5,2)/(4,2) pointed at EMPTY sheet cells (rendered as holes), and NW/NE
// were swapped left/right.
const CORNER_NW: SpriteCoords = { x: 4, y: 0 } // grass N+W, dirt wedge SE
const CORNER_NE: SpriteCoords = { x: 5, y: 0 } // grass N+E, dirt wedge SW
const CORNER_SW: SpriteCoords = { x: 4, y: 1 } // grass S+W, dirt wedge NE
const CORNER_SE: SpriteCoords = { x: 5, y: 1 } // grass S+E, dirt wedge NW

// Inner corner tiles (grass nibble on diagonal - the sprite shows WHERE the grass is)
// When diagonal X is missing (grass), use the sprite that has grass nibble at X
const INNER_NW: SpriteCoords = { x: 1, y: 2 } // grass nibble in NW corner
const INNER_NE: SpriteCoords = { x: 3, y: 2 } // grass nibble in NE corner
const INNER_SW: SpriteCoords = { x: 4, y: 4 } // grass nibble in SW corner
const INNER_SE: SpriteCoords = { x: 5, y: 4 } // grass nibble in SE corner

/**
 * Get the sprite for a single 16px subtile based on its neighbors.
 * This is simpler than the 32px version because each subtile only needs
 * to check 3 neighbors (2 cardinal + 1 diagonal for its corner).
 *
 * @param hasCardinal1 - Is the first cardinal neighbor (N or S) a path?
 * @param hasCardinal2 - Is the second cardinal neighbor (E or W) a path?
 * @param hasDiagonal - Is the diagonal neighbor a path?
 * @param position - Which subtile position (NW, NE, SW, SE)
 */
function getSubtileSprite(
  hasCardinal1: boolean,
  hasCardinal2: boolean,
  hasDiagonal: boolean,
  position: "NW" | "NE" | "SW" | "SE",
): SpriteCoords {
  // If both cardinals are path and diagonal is path = center dirt
  if (hasCardinal1 && hasCardinal2 && hasDiagonal) {
    return DIRT_SPRITE
  }

  // If both cardinals are path but diagonal is not = inner corner
  if (hasCardinal1 && hasCardinal2 && !hasDiagonal) {
    switch (position) {
      case "NW": return INNER_NW
      case "NE": return INNER_NE
      case "SW": return INNER_SW
      case "SE": return INNER_SE
    }
  }

  // If only one cardinal is path = edge tile
  if (hasCardinal1 && !hasCardinal2) {
    // Cardinal1 is path (N for NW/NE, S for SW/SE)
    switch (position) {
      case "NW": return EDGE_W // grass to west
      case "NE": return EDGE_E // grass to east
      case "SW": return EDGE_W // grass to west
      case "SE": return EDGE_E // grass to east
    }
  }

  if (!hasCardinal1 && hasCardinal2) {
    // Cardinal2 is path (W for NW/SW, E for NE/SE)
    switch (position) {
      case "NW": return EDGE_N // grass to north
      case "NE": return EDGE_N // grass to north
      case "SW": return EDGE_S // grass to south
      case "SE": return EDGE_S // grass to south
    }
  }

  // Neither cardinal is path = outer corner
  if (!hasCardinal1 && !hasCardinal2) {
    switch (position) {
      case "NW": return CORNER_NW
      case "NE": return CORNER_NE
      case "SW": return CORNER_SW
      case "SE": return CORNER_SE
    }
  }

  // Fallback
  return DIRT_SPRITE
}

/**
 * Get the 2x2 subtile grid for a path tile at the SUBTILE level.
 * This is called during tilemap generation to compute all 4 subtiles
 * for a logical 32px path tile.
 *
 * @param hasN - Is the north SUBTILE a path?
 * @param hasE - Is the east SUBTILE a path?
 * @param hasS - Is the south SUBTILE a path?
 * @param hasW - Is the west SUBTILE a path?
 * @param hasNE - Is the northeast SUBTILE a path?
 * @param hasSE - Is the southeast SUBTILE a path?
 * @param hasSW - Is the southwest SUBTILE a path?
 * @param hasNW - Is the northwest SUBTILE a path?
 */
export function getSubtileGridForPath(
  hasN: boolean,
  hasE: boolean,
  hasS: boolean,
  hasW: boolean,
  hasNE: boolean,
  hasSE: boolean,
  hasSW: boolean,
  hasNW: boolean,
): SubtileGrid {
  return [
    [
      getSubtileSprite(hasN, hasW, hasNW, "NW"),
      getSubtileSprite(hasN, hasE, hasNE, "NE"),
    ],
    [
      getSubtileSprite(hasS, hasW, hasSW, "SW"),
      getSubtileSprite(hasS, hasE, hasSE, "SE"),
    ],
  ]
}

/**
 * Get the subtile grid for a grass tile.
 * All 4 subtiles are the same grass sprite.
 */
export function getSubtileGridForGrass(): SubtileGrid {
  return [
    [GRASS_SPRITE, GRASS_SPRITE],
    [GRASS_SPRITE, GRASS_SPRITE],
  ]
}

// Decoration types
// Every sprite region below was visually verified against its sheet
// (the previous table's coordinates were largely wrong and never shipped -
// no level used sheet decorations until the generated tilemaps).
export type DecorationTileType =
  // Blocking props (unbuildable; most also block movement)
  | "rock_form_dirt"
  | "rock_form_grass"
  | "crystal_blue"
  | "red_plant"
  | "red_plant_spiky"
  | "bush_berry_blue"
  | "stones_small"
  // Cosmetic ground details (walkable and buildable)
  | "dirt_clearing"
  | "dirt_mound"
  | "flower_blue"
  | "flowers_white"
  | "flowers_orange"
  | "flowers_red"
  | "sparkle"
  | "dirt_speckles"
  | "grass_tufts"

// Decoration definitions
export interface DecorationDef {
  sheet: "treesRocks" | "details" | "ground"
  coords: SpriteCoords
  anchorX?: number
  anchorY?: number
  impassable: boolean
  buildable: boolean
  zIndex?: number
}

// Decoration sprite regions (coords/width/height in 16px sheet tiles;
// drawn at 2x so one sheet tile covers one 32px logical tile)
export const DECORATION_DEFS: Record<DecorationTileType, DecorationDef> = {
  // --- blocking props ---
  rock_form_dirt: {
    sheet: "treesRocks",
    coords: { x: 0, y: 15, width: 4, height: 4 },
    impassable: true,
    buildable: false,
    zIndex: 8,
  },
  rock_form_grass: {
    sheet: "treesRocks",
    coords: { x: 8, y: 15, width: 4, height: 4 },
    impassable: true,
    buildable: false,
    zIndex: 8,
  },
  crystal_blue: {
    sheet: "treesRocks",
    coords: { x: 8, y: 12, width: 3, height: 2 },
    impassable: true,
    buildable: false,
    zIndex: 6,
  },
  red_plant: {
    sheet: "treesRocks",
    coords: { x: 10, y: 5, width: 2, height: 2 },
    impassable: false,
    buildable: false,
    zIndex: 5,
  },
  red_plant_spiky: {
    sheet: "treesRocks",
    coords: { x: 0, y: 13, width: 2, height: 2 },
    impassable: false,
    buildable: false,
    zIndex: 5,
  },
  bush_berry_blue: {
    sheet: "treesRocks",
    coords: { x: 13, y: 5, width: 2, height: 3 },
    impassable: false,
    buildable: false,
    zIndex: 5,
  },
  stones_small: {
    sheet: "treesRocks",
    coords: { x: 8, y: 25, width: 2, height: 2 },
    impassable: false,
    buildable: false,
    zIndex: 4,
  },
  // --- cosmetic ground details ---
  dirt_clearing: {
    sheet: "ground",
    coords: { x: 8, y: 7, width: 3, height: 3 },
    impassable: false,
    buildable: true,
    zIndex: 0,
  },
  dirt_mound: {
    sheet: "details",
    coords: { x: 2, y: 8, width: 2, height: 2 },
    impassable: false,
    buildable: true,
    zIndex: 1,
  },
  flower_blue: {
    sheet: "details",
    coords: { x: 0, y: 10, width: 1, height: 1 },
    impassable: false,
    buildable: true,
    zIndex: 2,
  },
  flowers_white: {
    sheet: "details",
    coords: { x: 9, y: 9, width: 1, height: 1 },
    impassable: false,
    buildable: true,
    zIndex: 2,
  },
  flowers_orange: {
    sheet: "details",
    coords: { x: 8, y: 10, width: 2, height: 1 },
    impassable: false,
    buildable: true,
    zIndex: 2,
  },
  flowers_red: {
    sheet: "details",
    coords: { x: 10, y: 10, width: 2, height: 1 },
    impassable: false,
    buildable: true,
    zIndex: 2,
  },
  sparkle: {
    sheet: "details",
    coords: { x: 7, y: 10, width: 1, height: 1 },
    impassable: false,
    buildable: true,
    zIndex: 2,
  },
  dirt_speckles: {
    sheet: "details",
    coords: { x: 4, y: 12, width: 2, height: 1 },
    impassable: false,
    buildable: true,
    zIndex: 1,
  },
  grass_tufts: {
    sheet: "details",
    coords: { x: 0, y: 13, width: 3, height: 1 },
    impassable: false,
    buildable: true,
    zIndex: 1,
  },
}

// Level tile data structure
export interface LevelTile {
  ground: GroundTileType
  subtiles?: SubtileGrid // Pre-computed 2x2 subtile grid for rendering
}

// Level decoration placement
export interface LevelDecoration {
  type: DecorationTileType
  tileX: number
  tileY: number
}

// Object decoration (individual PNG images, not from spritesheets)
export interface ObjectDecoration {
  imagePath: string      // Path to PNG file (e.g. "/images/biomes/grassland/objects/Tree1.png")
  tileX: number          // Position in tile coordinates
  tileY: number
  widthTiles: number     // Size in tiles: 1, 2, or 4
  heightTiles: number    // Size in tiles: 1, 2, or 4
  zIndex: number         // Render order (higher = on top)
}

// Complete level tilemap data
export interface LevelTilemap {
  width: number
  height: number
  tiles: LevelTile[][]
  decorations: LevelDecoration[]
  objectDecorations?: ObjectDecoration[] // Individual image objects (unbuildable)
}
